/* eslint-disable prefer-promise-reject-errors */
import { useMutation, useLazyQuery } from "@apollo/client";
import { Button, Form, Radio, message } from "antd";
import { PlusCircleFilled } from "@ant-design/icons";
import { isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import PhoneInputWithCountrySelect, { isValidPhoneNumber } from "react-phone-number-input";
import { useDispatch } from "react-redux";
import { UPDATE_LEAD_V2 } from "../../../graphql/mutation";
import { GET_LEAD_DETAIL_CLICK } from "../../../graphql/query";
import { serializePhoneNumbers } from "../../../library/utils";
import { updateLeadInList } from "../../../redux/slices/leadSlice";
import AdditionalPhoneForm from "../common/AdditionalPhoneForm";

export const PhoneFormJsx = ({
  phones,
  phoneRef,
  selectedLead,
  setSelectedLead,
  phoneArr,
  showPrimary = true,
  onClose,
}) => {
  console.log("Rendering PhoneFormJsx with phones:", phoneArr, phones);
  const close = () => {
    if (onClose) {
      onClose();
    } else {
      phoneRef?.current?.click();
    }
  };
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);

  const [getLeadById] = useLazyQuery(GET_LEAD_DETAIL_CLICK);
  // Separate "Self" phones from additional phones
  const selfPhones = phones.filter(
    item => !item.relationshipType || item.relationshipType === "Self"
  );
  const additionalPhonesData = phones.filter(
    item => item.relationshipType && item.relationshipType !== "Self"
  );

  const [phoneData, setphoneData] = useState(
    selfPhones.map(item => {
      const temp = structuredClone(item);
      delete temp.potentialLeads;
      delete temp.leadId;
      return temp;
    })
  );
  const [additionalPhones, setAdditionalPhones] = useState(
    additionalPhonesData.map((item, index) => ({
      id: item.id || Math.random(),
      phone: item.phone,
      phoneType: item.phoneType,
      relation: item.relationshipType,
      firstName: item.firstName || "",
      lastName: item.lastName || "",
      note: item.note || "",
      isDeleted: false,
    }))
  );
  // primaryPhoneType can be: "Mobile", "Work", "Other" (for self phones) or "additional_0", "additional_1", etc. (for additional phones)
  const [primaryPhoneType, setprimaryPhoneType] = useState(phoneArr[0]);
  const [updateLeadInformation, { loading: updateLeadLoader }] = useMutation(UPDATE_LEAD_V2);

  // Function to collect additional phones from form
  const collectAdditionalPhones = () => {
    const formValues = form.getFieldsValue();
    const additionalPhonesData = [];

    additionalPhones.forEach((phoneItem, index) => {
      // If item is marked as deleted, send complete object with isDeleted: true
      if (phoneItem.isDeleted) {
        // Only include if it's an existing record from database
        if (phoneItem.id && !phoneItem.id.toString().startsWith("0.")) {
          additionalPhonesData.push({
            id: phoneItem.id,
          });
        }
      } else {
        // For non-deleted items, get current form values
        const phone = formValues[`additional_phone_${index}`];
        const phoneType = formValues[`additional_phone_type_${index}`];
        const relation = formValues[`additional_phone_relation_${index}`];
        const firstName = formValues[`additional_phone_firstName_${index}`];
        const lastName = formValues[`additional_phone_lastName_${index}`];
        const note = formValues[`additional_phone_note_${index}`];

        if (phone) {
          // Check if this additional phone is the primary one
          const isThisPrimary = primaryPhoneType === `additional_${index}`;
          const phoneData = {
            phone,
            phoneType,
            relationshipType: relation || "Other",
            firstName,
            lastName,
            note,
            isPrimary: isThisPrimary,
          };
          // Include id if it exists (for updating existing records)
          if (phoneItem.id && !phoneItem.id.toString().startsWith("0.")) {
            phoneData.id = phoneItem.id;
          }
          additionalPhonesData.push(phoneData);
        }
      }
    });

    return additionalPhonesData;
  };

  const handleSavePhone = async () => {
    if (isSaving) return; // Prevent multiple clicks

    // Validate self phones
    for (let phone = 0; phone < phoneData.length; phone++) {
      const element = phoneData[phone];
      if (!isEmpty(element.phone) && !isValidPhoneNumber(element.phone)) {
        return message.error(`Please enter a valid ${element.phoneType} phone number`);
      }
    }

    // Collect additional phones
    const collectedAdditionalPhones = collectAdditionalPhones();

    // Validate additional phones
    for (let phone = 0; phone < collectedAdditionalPhones.length; phone++) {
      const element = collectedAdditionalPhones[phone];
      if (!isEmpty(element.phone) && !isValidPhoneNumber(element.phone)) {
        return message.error(`Please enter a valid ${element.phoneType} phone number`);
      }
    }

    // Edge case: primary radio points to an empty row but other rows have values.
    if (showPrimary) {
      let primaryHasValue = false;
      if (phoneArr.includes(primaryPhoneType)) {
        const sp = phoneData.find(p => p.phoneType === primaryPhoneType);
        primaryHasValue = !!sp && !isEmpty(sp.phone);
      } else if (primaryPhoneType?.startsWith?.("additional_")) {
        const idx = parseInt(primaryPhoneType.replace("additional_", ""), 10);
        const v = form.getFieldValue(`additional_phone_${idx}`);
        primaryHasValue = !!v && !isEmpty(v);
      }
      if (!primaryHasValue) {
        const anyHasValue =
          phoneData.some(p => !isEmpty(p.phone)) || collectedAdditionalPhones.some(p => p.phone);
        if (anyHasValue) {
          return message.error(
            "The phone marked as primary is empty. Please enter a value or mark a non-empty phone as primary."
          );
        }
      }
    }

    // Merge self phones with additional phones
    // Check if primary is a self phone type or an additional phone
    const isPrimarySelfPhone = phoneArr.includes(primaryPhoneType);
    const allPhones = [
      ...phoneData.map(item => {
        const temp = structuredClone(item);

        const phoneData = {
          phone: temp.phone,
          phoneType: temp.phoneType,
          // Self phone is primary only if primaryPhoneType matches AND it's not an additional phone
          isPrimary: showPrimary
            ? isPrimarySelfPhone && temp.phoneType === primaryPhoneType
            : false,
          relationshipType: "Self",
          firstName: temp.firstName || "",
          lastName: temp.lastName || "",
          note: temp.note || "",
        };
        // Include id if it exists (for updating existing records)
        if (temp.id && !temp.id.toString().startsWith("0.")) {
          phoneData.id = temp.id;
        }
        return phoneData;
      }),
      ...collectedAdditionalPhones,
    ];

    setIsSaving(true);
    try {
      const res = await updateLeadInformation({
        variables: {
          leadId: selectedLead.id,
          firstName: selectedLead?.firstName,
          lastName: selectedLead?.lastName,
          leadAddressObj:
            selectedLead?.addresses.length > 0
              ? {
                  addressLine1: selectedLead?.addresses[0].addressLine1,
                  addressLine2: selectedLead?.addresses[0].addressLine2,
                  city: selectedLead?.addresses[0].city,
                  state: selectedLead?.addresses[0].state,
                  country: selectedLead?.addresses[0].country,
                  postalCode: selectedLead?.addresses[0].postalCode,
                  locationText: "Main Branch",
                }
              : null,
          phones: allPhones,
          isSyncCheck: false,
          isPushCrm: false,
        },
      });

      if (res.data.updateLead.statusCode === 200) {
        message.success(res.data.updateLead.message);

        try {
          const leadRes = await getLeadById({
            variables: {
              leadId: selectedLead.id,
            },
            fetchPolicy: "network-only",
          });

          if (leadRes?.data?.lead) {
            // Update selectedLead with new phone data
            const updatedLead = {
              ...selectedLead,
              phoneNumbers: leadRes?.data?.lead?.phoneNumbers,
            };
            setSelectedLead(updatedLead);
            dispatch(updateLeadInList(updatedLead));
            close();
          } else {
            console.error("getLeadById returned no data");
            close();
          }
        } catch (error) {
          console.error("Error fetching updated lead data:", error);
          // Close drawer even if getLeadById fails
          close();
        }
      } else {
        message.error(res.data.updateLead.message);
      }
    } catch (error) {
      console.error("Error updating phone:", error);
      message.error("Failed to update phone numbers");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    // Update phoneData state with all self phones
    const updatedPhoneData = selfPhones.map(item => {
      const temp = structuredClone(item);
      delete temp.potentialLeads;
      delete temp.leadId;
      return temp;
    });
    setphoneData(updatedPhoneData);

    // Update additionalPhones state when phones prop changes (important for edit mode)
    const updatedAdditionalPhones = additionalPhonesData.map(item => ({
      id: item.id || Math.random(),
      phone: item.phone,
      phoneType: item.phoneType,
      relation: item.relationshipType,
      firstName: item.firstName || "",
      lastName: item.lastName || "",
      note: item.note || "",
      isPrimary: item.isPrimary || false,
      isDeleted: false,
    }));
    setAdditionalPhones(updatedAdditionalPhones);

    // Set form field values for existing phones
    const existingPhones = selfPhones.filter(item => item.id);
    if (existingPhones.length > 0) {
      const obj = selfPhones
        .filter(item => item.id)
        .map(item => ({ [item.phoneType]: item.phone }));

      if (obj) form.setFieldsValue(serializePhoneNumbers(obj));
    }

    // Set primary phone type - check both self phones and additional phones
    const primarySelfPhone = selfPhones.find(item => item.isPrimary);
    if (primarySelfPhone) {
      setprimaryPhoneType(primarySelfPhone.phoneType);
    } else {
      // Check if any additional phone is primary
      const primaryAdditionalIndex = additionalPhonesData.findIndex(item => item.isPrimary);
      if (primaryAdditionalIndex !== -1) {
        setprimaryPhoneType(`additional_${primaryAdditionalIndex}`);
      } else if (selfPhones.length > 0) {
        // Default to first self phone type if no primary is set
        setprimaryPhoneType(selfPhones[0]?.phoneType || phoneArr[0]);
      }
    }

    // Populate additional phone fields
    if (additionalPhonesData.length > 0) {
      const additionalPhoneFormValues = {};
      additionalPhonesData.forEach((phone, index) => {
        additionalPhoneFormValues[`additional_phone_${index}`] = phone.phone;
        additionalPhoneFormValues[`additional_phone_type_${index}`] = phone.phoneType;
        additionalPhoneFormValues[`additional_phone_relation_${index}`] = phone.relationshipType;
        additionalPhoneFormValues[`additional_phone_firstName_${index}`] = phone.firstName || "";
        additionalPhoneFormValues[`additional_phone_lastName_${index}`] = phone.lastName || "";
        additionalPhoneFormValues[`additional_phone_note_${index}`] = phone.note || "";
      });
      form.setFieldsValue(additionalPhoneFormValues);
    }
  }, [phones]);
  const getPhone = type => {
    const p = phoneData.find(item => item.phoneType === type);
    return p?.phone;
  };
  const setPhone = (value, type) => {
    const index = phoneData.findIndex(item => item.phoneType === type);
    if (index !== -1) {
      setphoneData(prev => {
        const phones = structuredClone(prev);
        phones[index] = {
          ...phones[index],
          phone: value,
        };
        return phones;
      });
    } else {
      setphoneData([...phoneData, { phoneType: type, phone: value }]);
    }
  };

  // Helper to get additional phone value from form
  const getAdditionalPhoneValue = index => {
    const formValues = form.getFieldsValue();
    return formValues[`additional_phone_${index}`];
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleSavePhone} className="w-full p-4">
      <Radio.Group
        value={primaryPhoneType}
        onChange={e => {
          const selectedValue = e.target.value;

          // Check if it's a self phone type or additional phone
          if (phoneArr.includes(selectedValue)) {
            // Self phone type
            const phoneValue = getPhone(selectedValue);
            if (!phoneValue || isEmpty(phoneValue)) {
              message.warning(
                `Please enter a ${selectedValue} phone number before marking it as primary`
              );
              return;
            }
          } else if (selectedValue.startsWith("additional_")) {
            // Additional phone
            const index = parseInt(selectedValue.replace("additional_", ""), 10);
            const phoneValue = getAdditionalPhoneValue(index);
            if (!phoneValue || isEmpty(phoneValue)) {
              message.warning("Please enter a phone number before marking it as primary");
              return;
            }
          }

          setprimaryPhoneType(selectedValue);
        }}
        className="w-full"
      >
        {/* Main Phone Numbers Section */}
        <div className="bg-gray-50 p-3 rounded-md mb-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Phone Numbers</h3>
            {showPrimary && <span className="text-xs text-gray-500">Select Primary</span>}
          </div>

          {phoneArr.map((item, index) => {
            return (
              <div key={index} className="w-full mb-3 flex items-start gap-2">
                <div className="flex-1">
                  <Form.Item
                    rules={[
                      {
                        required: showPrimary ? item === primaryPhoneType : true,
                        message: `Please enter ${
                          showPrimary ? primaryPhoneType : item
                        } phone number`,
                      },
                    ]}
                    name={item}
                    label={
                      <span className="text-xs font-medium text-gray-600">Phone ({item})</span>
                    }
                    className="mb-0"
                  >
                    <PhoneInputWithCountrySelect
                      onChange={value => {
                        if (isEmpty(value)) {
                          form.setFieldValue(item, "");
                          setPhone("", item);
                        } else {
                          form.setFieldValue(item, value);
                          setPhone(value, item);
                        }
                      }}
                      value={getPhone(item)}
                      name={item}
                      className="aboutThisLead"
                    />
                  </Form.Item>
                </div>
                {showPrimary && <Radio value={item} className="flex-shrink-0 mt-9" />}
              </div>
            );
          })}
        </div>

        <h3 className="font-semibold text-gray-700 mb-3 py-2 border-b border-gray-300">
          Additional Contacts
        </h3>
        {/* Additional Phones Section */}
        {additionalPhones.filter(phone => !phone.isDeleted).length > 0 && (
          <div className="mb-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="space-y-3">
              {additionalPhones.map((phoneItem, index) => {
                if (phoneItem.isDeleted) return null;
                return (
                  <AdditionalPhoneForm
                    key={`phone_key__${index}`}
                    item={phoneItem}
                    index={index}
                    onDelete={() => {
                      if (primaryPhoneType === `additional_${index}`) {
                        message.error(
                          "This phone is marked as primary. Please mark another phone as primary before deleting."
                        );
                        return;
                      }
                      setAdditionalPhones(prev => {
                        const itemToDelete = prev.find(item => item.id === phoneItem.id);
                        // If item has a numeric id (from database), mark as deleted
                        // Otherwise just filter it out (it's a new item not yet saved)
                        if (itemToDelete && !itemToDelete.id.toString().startsWith("0.")) {
                          return prev.map(item =>
                            item.id === phoneItem.id ? { ...item, isDeleted: true } : item
                          );
                        }

                        return prev.filter(item => item.id !== phoneItem.id);
                      });
                    }}
                    showPrimary={showPrimary}
                    primaryValue={`additional_${index}`}
                    isPrimary={primaryPhoneType === `additional_${index}`}
                  />
                );
              })}
            </div>
          </div>
        )}
      </Radio.Group>

      {/* Add Additional Phone Button */}
      <div
        className="flex w-[200px] items-center justify-center gap-2 p-3 mb-4 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
        onClick={() => {
          setAdditionalPhones(prev => {
            const newState = [...prev];
            newState.push({
              id: Math.random(),
              phone: "",
              phoneType: "",
              relation: "",
              firstName: "",
              lastName: "",
              note: "",
            });
            return newState;
          });
        }}
      >
        <PlusCircleFilled className="text-blue-500" />
        <span className="text-sm font-medium text-blue-500">Add Additional Phone</span>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 pt-3 border-t border-gray-200">
        <Button loading={isSaving || updateLeadLoader} type="primary" htmlType="submit">
          Save Changes
        </Button>
        <Button onClick={close} disabled={isSaving}>
          Cancel
        </Button>
      </div>
    </Form>
  );
};
